import { useAtomValue } from "@effect/atom-react";
import { queryAtomEffect } from "@crm/shared/query-atom/effect";
import { rpcEffectCaller } from "@crm/rpc-client";
import { ReviewCoursesRpc } from "@crm/rpc-contract/groups/review-courses";
import type { GetReviewCoursesInput } from "../../domain/review-course-inputs";

// 类型化 RPC Effect 调用器（错误通道保留）
const reviewCoursesRpc = rpcEffectCaller(ReviewCoursesRpc);

// =============================================================================
// Review Courses Queries（Effect-native）
//
// 课程/模块/课时/资源四域按响应键声明式失效：任一 mutation 成功后经
// reactivityKeys 驱动对应域全部订阅原子刷新（替代 invalidateQueries）。
// =============================================================================

/**
 * Hook: Get all review courses
 *
 * 权限：
 * - Staff 角色：可以看到所有复习课程
 * - Student 角色：只能看到通用课程 + 自己参加的课程
 */
export type ReviewCourseFilters = NonNullable<GetReviewCoursesInput>;

const reviewCoursesAtom = queryAtomEffect(
  (key: readonly [ReviewCourseFilters | undefined]) =>
    reviewCoursesRpc.getReviewCourses(key[0]),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["reviewCourse"] }, // 5 分钟
);

export function useReviewCourses(filters?: ReviewCourseFilters) {
  return useAtomValue(reviewCoursesAtom([filters]));
}

// =============================================================================
// Review Modules Queries
// =============================================================================

/**
 * Hook: Get all review modules
 */
const reviewModulesAtom = queryAtomEffect(
  (_key: readonly []) => reviewCoursesRpc.getReviewModules(undefined),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["reviewModule"] }, // 5 分钟
);

export function useReviewModules() {
  return useAtomValue(reviewModulesAtom([]));
}

/**
 * Hook: Get modules by course ID
 *
 * @param courseId 复习课程 ID
 */
const reviewModulesByCourseAtom = queryAtomEffect(
  (key: readonly [string]) =>
    reviewCoursesRpc.getReviewModulesByCourse({
      courseId: key[0],
    }),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["reviewModule"] },
);

export function useReviewModulesByCourse(courseId: string) {
  return useAtomValue(reviewModulesByCourseAtom([courseId], !!courseId));
}

// =============================================================================
// Review Lessons Queries
// =============================================================================

/**
 * Hook: Get all review lessons
 */
const reviewLessonsAtom = queryAtomEffect(
  (_key: readonly []) => reviewCoursesRpc.getReviewLessons(undefined),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["reviewLesson"] },
);

export function useReviewLessons() {
  return useAtomValue(reviewLessonsAtom([]));
}

/**
 * Hook: Get lessons by module ID
 *
 * @param moduleId 模块 ID
 */
const reviewLessonsByModuleAtom = queryAtomEffect(
  (key: readonly [string]) =>
    reviewCoursesRpc.getReviewLessonsByModule({
      moduleId: key[0],
    }),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["reviewLesson"] },
);

export function useReviewLessonsByModule(moduleId: string) {
  return useAtomValue(reviewLessonsByModuleAtom([moduleId], !!moduleId));
}

/**
 * Hook: Get lessons by course ID
 *
 * @param courseId 复习课程 ID
 */
const reviewLessonsByCourseAtom = queryAtomEffect(
  (key: readonly [string]) =>
    reviewCoursesRpc.getReviewLessonsByCourse({
      courseId: key[0],
    }),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["reviewLesson"] },
);

export function useReviewLessonsByCourse(courseId: string) {
  return useAtomValue(reviewLessonsByCourseAtom([courseId], !!courseId));
}
